// The daily brief: what arrived since the last visit across followed feeds, best fits first, what is
// coming up, and what changed on tracked records.
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";
import { api, errorText } from "../api/client";
import type { Brief as BriefData, NoticeItem } from "../api/types";
import { useUI } from "../app/context";
import { keys, useSession } from "../app/queries";
import { Badge } from "../components/Badge";
import { GlobalSearch } from "../components/GlobalSearch";
import { DuePill, NoticeRow, PickCard, UpcomingRow } from "../components/Rows";
import { ALERT_KINDS } from "../lib/alerts";
import { checkedText, first, when } from "../lib/format";
import { FeedEditor } from "./FeedEditor";

export function Brief() {
  const me = useSession();
  const brief = useQuery({ queryKey: keys.brief, queryFn: () => api<BriefData>("/api/brief") });
  const [creating, setCreating] = useState(false);
  if (brief.isPending) return <p className="empty">Loading your brief…</p>;
  if (brief.isError) return <p className="empty">{errorText(brief.error, "Couldn’t load your brief. Reload to try again.")}</p>;
  const b = brief.data;
  const hour = new Date().getHours();
  const greet = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const since = when(b.since);
  const feedsWord = b.followed === 1 ? "feed" : "feeds";
  const scope = b.following_any ? "you follow" : "in your sidebar";
  const src = b.sources;
  return (
    <>
      <div className="top">
        <div className="date">{new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</div>
        <GlobalSearch />
      </div>
      <h1>
        {greet}, {first(me.user.name)}
      </h1>
      <p className="lede">
        {b.followed && !b.new ? (
          <>
            Nothing new has come in since {since} across the {b.followed} {feedsWord} {scope}.
            {b.changes.length ? " Some things you track have changed." : ""}
          </>
        ) : b.followed ? (
          <>
            <b>
              {b.new} new record{b.new === 1 ? "" : "s"}
            </b>{" "}
            came in since {since} across the {b.followed} {feedsWord} {scope}
            {src.length
              ? `, from ${src.slice(0, 3).join(", ")}${src.length > 3 ? ` and ${src.length - 3} other source${src.length > 4 ? "s" : ""}` : ""}`
              : ""}
            . {b.picks.length ? `These ${b.picks.length} fit best.` : ""}
          </>
        ) : (
          "You have no feeds yet. Create one to describe the work you want, and GovChew will bring you matching opportunities every day."
        )}
      </p>
      <Collection c={b.collection} />
      {b.changes.length ? (
        <section>
          <div className="sec-head">
            <h2>Changes to what you track</h2>
            <Link to="/alerts">All alerts</Link>
          </div>
          <div className="rows">
            {b.changes.map((a) => a.notice && <ChangeRow key={a.id} kind={a.kind} body={a.body} n={a.notice} />)}
          </div>
        </section>
      ) : null}
      {b.picks.length ? (
        <section>
          <div className="sec-head">
            <h2>Worth reading first</h2>
            <span>Best fits among what's new</span>
          </div>
          <div>
            {b.picks.map((n) => (
              <PickCard key={n.key} n={n as NoticeItem & { feed: NonNullable<NoticeItem["feed"]> }} />
            ))}
          </div>
        </section>
      ) : null}
      {b.sections.map((s) => (
        <section key={s.feed.id}>
          <div className="sec-head">
            <h2>New in {s.feed.name}</h2>
            <span>{s.feed.mine ? "Your feed" : `${s.feed.owner_name}’s feed`}</span>
          </div>
          <div className="rows">
            {s.items.map((n) => (
              <NoticeRow key={n.key} n={{ ...n, feed: s.feed }} inBrief />
            ))}
          </div>
          {s.total > s.items.length ? (
            <Link className="more-link" to={`/feeds/${s.feed.id}?view=new`}>
              See all {s.total} new in {s.feed.name}
            </Link>
          ) : null}
        </section>
      ))}
      {b.coming_up.length ? (
        <section>
          <div className="sec-head">
            <h2>Coming up</h2>
            <span>
              Forecasts in your feeds expected in the next 90 days
              {b.coming_up_total > b.coming_up.length ? `, ${b.coming_up_total} in all` : ""}
            </span>
          </div>
          <div className="rows">
            {b.coming_up.map((n) => (
              <UpcomingRow key={n.key} n={n} inBrief />
            ))}
          </div>
        </section>
      ) : null}
      {b.new < 5 && b.closing.length ? (
        <section>
          <div className="sec-head">
            <h2>Closing in the next two weeks</h2>
            <span>From your feeds</span>
          </div>
          <div className="rows">
            {b.closing.map((n) => (
              <NoticeRow key={n.key} n={n} inBrief />
            ))}
          </div>
        </section>
      ) : null}
      {b.new < 5 && b.latest.length ? (
        <section>
          <div className="sec-head">
            <h2>Latest in your feeds</h2>
            <span>Most recently found, still open</span>
          </div>
          <div className="rows">
            {b.latest.map((n) => (
              <NoticeRow key={n.key} n={n} inBrief />
            ))}
          </div>
        </section>
      ) : null}
      {b.followed ? null : (
        <p>
          <button className="btn primary" id="first-feed" onClick={() => setCreating(true)}>
            Create a feed
          </button>
        </p>
      )}
      <FeedEditor feed={null} open={creating} onClose={() => setCreating(false)} />
    </>
  );
}

function ChangeRow({ kind, body, n }: { kind: string; body: string; n: NoticeItem }) {
  const { openPanel } = useUI();
  return (
    <div className="row change" data-key={n.key} role="button" tabIndex={0} onClick={() => openPanel({ kind: "notice", key: n.key, feedId: null })}>
      <Badge agency={n.agency} small />
      <div style={{ minWidth: 0 }}>
        <div className="t">{n.title}</div>
        <div className="m">
          <span className="new">{(ALERT_KINDS[kind] ?? [kind])[0]}</span>
          {" "}
          {body}
        </div>
      </div>
      <div>
        <DuePill n={n} />
      </div>
      <div className="fitcol" />
    </div>
  );
}

function Collection({ c }: { c: BriefData["collection"] }) {
  const me = useSession();
  const client = useQueryClient();
  const { showToast } = useUI();
  const [checking, setChecking] = useState(false);
  if (c.running) return <p className="notice-bar">Checking sources for new records now. This takes a few minutes; new arrivals will appear here.</p>;
  if (!c.stale)
    return (
      <p className="muted-s" style={{ marginTop: 10 }}>
        {checkedText(c.last)}
        {c.scheduled && c.next_in != null ? ` Next check in about ${Math.max(1, Math.round(c.next_in / 60))} minutes.` : ""}
      </p>
    );
  const refresh = async () => {
    setChecking(true);
    showToast("Checking every source. This takes a few minutes.");
    try {
      await api("/sync", { method: "POST", body: { days: 7 } });
      showToast("Sources checked.");
    } catch (err) {
      showToast(errorText(err));
    }
    setChecking(false);
    client.invalidateQueries({ queryKey: keys.brief });
  };
  return (
    <p className="notice-bar warn">
      {checkedText(c.last)} New notices published since then aren't here yet.
      {me.superuser ? (
        <>
          {" "}
          <button className="btn" id="refresh-now" disabled={checking} onClick={refresh}>
            {checking ? "Checking…" : "Check sources now"}
          </button>
        </>
      ) : null}
    </p>
  );
}
